import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StoryShareButton } from "@/components/StoryShare";
import { MapPin, Instagram, Facebook, Music2, Quote, ArrowLeft, ArrowRight } from "lucide-react";

const COACH_INSTAGRAM_URL = "https://instagram.com/flow_state_method";

/**
 * Single-story permalink page (rendered at /story/:id). Used as the share target
 * so each story has its own URL with proper OG meta tags for rich link previews.
 */
export default function StoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stories/${id}`);
        if (!cancelled) setStory(data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Resolve the image URL. Story image URLs from the API are relative (/api/uploads/{id}).
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const absoluteImage = story?.image_url
    ? (story.image_url.startsWith("http") ? story.image_url : `${apiBase}${story.image_url}`)
    : "";

  useSEO({
    title: story ? `${story.headline} · Better Through Boxing` : "Story · Better Through Boxing",
    description: story ? (story.story || "").slice(0, 200) : "A community story from Better Through Boxing.",
    path: `/story/${id}`,
    ogImage: absoluteImage || undefined,
    keywords: ["boxing", "transformation story", "Better Through Boxing", story?.user_name, "Coach Chris"].filter(Boolean),
  });

  if (notFound) {
    return (
      <div className="App min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="story-detail-not-found">
          <h1 className="font-display uppercase text-4xl mb-4">Story not found</h1>
          <p className="text-zinc-400 mb-6">This story may have been removed or is awaiting review.</p>
          <Button onClick={() => navigate("/share-your-story")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm">
            Back to stories
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="App min-h-screen bg-[#050505] text-white" data-testid="story-detail-loading">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-500 text-sm">Loading…</main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid={`story-detail-${story.id}`}>
        {/* Breadcrumb */}
        <Link to="/share-your-story" className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-8" data-testid="story-detail-back">
          <ArrowLeft className="h-3 w-3" /> All stories
        </Link>

        {/* Hero meta */}
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Community story</div>
        <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] mb-6">{story.headline}</h1>
        <div className="flex items-center gap-3 mb-8 text-[11px] uppercase tracking-widest font-bold text-zinc-500">
          <span className="text-white">{story.user_name}</span>
          {story.city && (<><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {story.city}</span></>)}
        </div>

        {/* Story photo */}
        {story.image_url && (
          <figure className="mb-10 -mx-6 md:mx-0">
            <img src={story.image_url} alt={story.headline} className="w-full aspect-[16/10] object-cover border-y md:border border-[#27272A]" />
          </figure>
        )}

        {/* Story body */}
        <article className="prose prose-invert max-w-none mb-10">
          <p className="text-zinc-200 text-base md:text-lg leading-relaxed whitespace-pre-wrap">{story.story}</p>
        </article>

        {/* Moral / takeaway */}
        <div className="border-l-2 border-[#FF331F] pl-5 my-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">The moral</div>
          <div className="flex items-start gap-2">
            <Quote className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-1" />
            <p className="text-zinc-100 text-lg md:text-xl leading-relaxed italic whitespace-pre-wrap">{story.moral}</p>
          </div>
        </div>

        {/* Author socials */}
        {(story.social?.instagram || story.social?.tiktok || story.social?.facebook) && (
          <div className="border-t border-[#27272A] pt-6 mb-10">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Follow {story.user_name}</div>
            <div className="flex flex-wrap gap-2">
              {story.social.instagram && (
                <a href={/^https?:\/\//i.test(story.social.instagram) ? story.social.instagram : `https://instagram.com/${story.social.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-[#FF331F] border border-[#27272A] hover:border-[#FF331F] px-3 py-1.5" data-testid="story-detail-social-instagram">
                  <Instagram className="h-3.5 w-3.5" /> Instagram
                </a>
              )}
              {story.social.tiktok && (
                <a href={/^https?:\/\//i.test(story.social.tiktok) ? story.social.tiktok : `https://tiktok.com/@${story.social.tiktok.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-[#FF331F] border border-[#27272A] hover:border-[#FF331F] px-3 py-1.5" data-testid="story-detail-social-tiktok">
                  <Music2 className="h-3.5 w-3.5" /> TikTok
                </a>
              )}
              {story.social.facebook && (
                <a href={/^https?:\/\//i.test(story.social.facebook) ? story.social.facebook : `https://facebook.com/${story.social.facebook}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-[#FF331F] border border-[#27272A] hover:border-[#FF331F] px-3 py-1.5" data-testid="story-detail-social-facebook">
                  <Facebook className="h-3.5 w-3.5" /> Facebook
                </a>
              )}
            </div>
          </div>
        )}

        {/* Share + follow CTA */}
        <div className="border-t border-[#27272A] pt-8 flex items-center justify-between flex-wrap gap-4">
          <StoryShareButton story={story} />
          <a href={COACH_INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm" data-testid="story-detail-coach-ig">
            <Instagram className="h-4 w-4 mr-2" /> Follow Coach Chris
          </a>
        </div>

        {/* Bottom: share your own story */}
        <div className="border border-[#27272A] bg-[#0F0F0F] p-6 md:p-8 mt-12 text-center">
          <h3 className="font-display uppercase text-2xl tracking-tight mb-2">Boxing changed your life too?</h3>
          <p className="text-zinc-400 text-sm mb-4 max-w-md mx-auto">Share your story and inspire the next person to take the first step.</p>
          <Link to="/share-your-story" className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm" data-testid="story-detail-cta">
            Share yours <ArrowRight className="ml-2 h-3 w-3" />
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
